import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, List, ListOrdered, Link2, Code } from 'lucide-react';

const MentionList = forwardRef(({ items, command }, ref) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [items]);
  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') { setIdx((i) => (i + items.length - 1) % items.length); return true; }
      if (event.key === 'ArrowDown') { setIdx((i) => (i + 1) % items.length); return true; }
      if (event.key === 'Enter') { const it = items[idx]; if (it) command({ id: it.id, label: it.name }); return true; }
      return false;
    },
  }));
  if (!items.length) return <div className="rounded-xl border border-border bg-white px-3 py-2 text-xs text-muted-foreground shadow-lg">Tidak ada anggota</div>;
  return (
    <div className="min-w-[200px] overflow-hidden rounded-xl border border-border bg-white py-1 shadow-lg" data-testid="mention-list">
      {items.map((it, i) => <button type="button" key={it.id} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); command({ id: it.id, label: it.name }); }} className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${i === idx ? 'bg-muted' : ''}`} data-testid={`mention-item-${it.id}`}><span className="font-medium">{it.name}</span><span className="truncate text-[11px] text-muted-foreground">{it.email}</span></button>)}
    </div>
  );
});

// Popup di-mount di dalam wrapper editor (bukan body) agar bisa diklik di dalam Sheet/Dialog Radix (pointer-events & dismiss-outside).
const suggestion = (getMembers, getWrapper) => ({
  items: ({ query }) => getMembers().filter((m) => (m.name || '').toLowerCase().includes(query.toLowerCase())).slice(0, 8),
  render: () => {
    let comp; let el;
    const place = (props) => { const r = props.clientRect?.(); const w = getWrapper(); if (!r || !el || !w) return; const wr = w.getBoundingClientRect(); el.style.left = `${Math.max(0, Math.min(r.left - wr.left, wr.width - 220))}px`; el.style.top = `${r.bottom - wr.top + 4}px`; };
    return {
      onStart: (props) => { comp = new ReactRenderer(MentionList, { props, editor: props.editor }); el = document.createElement('div'); el.style.position = 'absolute'; el.style.zIndex = 60; el.setAttribute('data-testid', 'mention-popup'); el.appendChild(comp.element); (getWrapper() || document.body).appendChild(el); place(props); },
      onUpdate: (props) => { comp?.updateProps(props); place(props); },
      onKeyDown: (props) => (props.event.key === 'Escape' ? (el?.remove(), true) : comp?.ref?.onKeyDown(props) || false),
      onExit: () => { el?.remove(); comp?.destroy(); },
    };
  },
});

/** TipTap rich text editor; value/onChange are HTML strings. members=[{id,name,email}] enables @mention. */
export const RichEditor = ({ value = '', onChange, members = [], placeholder = '', minimal = false, autoFocus = false, testid = 'rich-editor', onMentionsChange }) => {
  const wrapRef = useRef(null);
  const membersRef = useRef(members);
  membersRef.current = members;
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [2, 3] } }), Link.configure({ openOnClick: false, autolink: true }), Placeholder.configure({ placeholder }),
      Mention.configure({ HTMLAttributes: { class: 'mention' }, suggestion: suggestion(() => membersRef.current, () => wrapRef.current), renderLabel: ({ node }) => `@${node.attrs.label || node.attrs.id}` })],
    content: value,
    autofocus: autoFocus,
    onUpdate: ({ editor: e }) => {
      onChange?.(e.isEmpty ? '' : e.getHTML());
      if (onMentionsChange) { const ids = []; e.state.doc.descendants((n) => { if (n.type.name === 'mention') ids.push(n.attrs.id); }); onMentionsChange([...new Set(ids)]); }
    },
  });
  useEffect(() => { if (editor && value !== editor.getHTML() && !editor.isFocused) editor.commands.setContent(value || '', false); }, [value, editor]);
  if (!editor) return null;
  const B = ({ on, active, children, label }) => <button type="button" onMouseDown={(e) => { e.preventDefault(); on(); }} className={`rounded-md p-1 ${active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}`} aria-label={label} title={label}>{children}</button>;
  const setLink = () => { const url = window.prompt('URL'); if (url === null) return; url ? editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run() : editor.chain().focus().unsetLink().run(); };
  return (
    <div ref={wrapRef} className="relative rounded-xl border border-border bg-white focus-within:ring-2 focus-within:ring-ring" data-testid={testid}>
      {!minimal && <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-1.5 py-1">
        <B on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} label="Bold"><Bold size={14} /></B>
        <B on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} label="Italic"><Italic size={14} /></B>
        <B on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} label="Bullet"><List size={14} /></B>
        <B on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} label="Numbered"><ListOrdered size={14} /></B>
        <B on={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} label="Code"><Code size={14} /></B>
        <B on={setLink} active={editor.isActive('link')} label="Link"><Link2 size={14} /></B>
        {members.length > 0 && <span className="ml-auto pr-1 text-[10px] text-muted-foreground">@ untuk mention</span>}
      </div>}
      <EditorContent editor={editor} className="rich-content px-3 py-2 text-sm" />
    </div>
  );
};

export const RichView = ({ html, className = '' }) => <div className={`rich-content text-sm ${className}`} dangerouslySetInnerHTML={{ __html: html || '' }} />;
